import {ILayerView, LayerInfo, LayerUpdateMetadata} from "@esWebAPI/es/model/Layer";
import {DatasourceManager} from "@esWebAPI/es/map/module/DatasourceManager";
import {AzureBubbleLayer} from "@esWebAPI/es/map/layer/azure/AzureBubbleLayer";
import {data, Map, MapErrorEvent, source} from "azure-maps-control";
import {ThematicStylerFactory} from "@esWebAPI/es/map/styler/ThematicStylerFactory";
import {MapLayer} from "@esWebAPI/es/map/layer/MapLayer";
import {IEventChannel} from "@esWebAPI/es/model/Models";
import {ExpressionFilterSpecification} from "@maplibre/maplibre-gl-style-spec";
import {ColorThematic, SizeThematic, SymbolThematic} from "@esWebAPI/es/model/Thematic";
import {AzureSymbolLayer} from "@esWebAPI/es/map/layer/azure/AzureSymbolLayer";
import {AzureImageSpriteManager} from "@esWebAPI/es/map/layer/AzureImageSpriteManager";
import {AzureLabelLayer} from "@esWebAPI/es/map/layer/azure/AzureLabelLayer";
import _, {isNaN} from "lodash";
import {GeomUtils} from "@esWebAPI/es/util/GeomUtils";
import {
    isBubbleLayer,
    isMouseoverLabellingEnabled,
    isPieClusteringOn
} from "@esWebAPI/es/map/layer/azure/AzureLayer";
import {PieClusterLayer} from "@esWebAPI/es/map/layer/PieClusterLayer";
import {ColorThematicStyler} from "@esWebAPI/es/map/styler/ColorThematicStyler";
import {SizeThematicStyler} from "@esWebAPI/es/map/styler/SizeThematicStyler";
import {ImageSpriteGenerator} from "@esWebAPI/es/map/layer/ImageSpriteGenerator";
import {SymbolThematicStyler} from "@esWebAPI/es/map/styler/SymbolThematicStyler";
import {AzureMouseoverLabelHandler} from "@esWebAPI/es/map/handler/AzureMouseoverLabelHandler";


export class PointLayer extends MapLayer {

    private readonly _bubbleLayer: AzureBubbleLayer;
    private readonly _symbolLayer: AzureSymbolLayer
    private readonly _labelLayer: AzureLabelLayer

    private _pieClusterLayer: PieClusterLayer|undefined;

    private readonly stylerFactory: ThematicStylerFactory;
    private readonly _colorStyler: ColorThematicStyler;
    private readonly _sizeStyler: SizeThematicStyler;
    private readonly _symbolStyler: SymbolThematicStyler;

    private _datasource: source.Source|undefined;

    private _colorThematic: ColorThematic|undefined;
    private _sizeThematic: SizeThematic|undefined;
    private _symbolThematic: SymbolThematic|undefined;

    private updatePromise = Promise.resolve();


    private handleSuperclusterExpired = _.debounce(this.reloadSupercluster.bind(this), 500);

    private debouncedOnUpdateThematic = _.debounce(this.onUpdateThematic.bind(this), 500);

    constructor(map: Map, layerView: ILayerView, layerInfo: LayerInfo,
                datasourceManager: DatasourceManager,
                spriteManager: AzureImageSpriteManager,
                stylerFactory: ThematicStylerFactory,
                eventChannel: IEventChannel,
                datasource? : source.Source,
                layerFilter?: ExpressionFilterSpecification,
                spriteGenerator?: ImageSpriteGenerator) {

        super(map, layerView, layerInfo, datasourceManager, eventChannel)

        this.stylerFactory = stylerFactory;
        this._colorStyler = stylerFactory.getColorStyler(layerView);
        this._sizeStyler = stylerFactory.getSizeStyler();
        this._symbolStyler = stylerFactory.getSymbolStyler();

        this._layerFilter = layerFilter;

        this._datasource = datasource;

        this._bubbleLayer = new AzureBubbleLayer(map, layerView, layerInfo, datasourceManager,
            this._colorStyler, this._sizeStyler, layerFilter);

        this._symbolLayer = new AzureSymbolLayer(map, layerView, layerInfo, datasourceManager, spriteManager,
            this._colorStyler, this._sizeStyler, this._symbolStyler, layerFilter, spriteGenerator);

        this._labelLayer = new AzureLabelLayer(map, layerView, layerInfo, datasourceManager, spriteManager, stylerFactory, layerFilter)

        this.createLayers().then(() => {

            es.api.getEventChannel().addListener('onUpdateThematic', this.debouncedOnUpdateThematic);

            this.map.events.add("error", (e: MapErrorEvent) => {
                if (this._datasource && this.datasourceManager.isPbf(this._datasource.getId()) &&
                    e.error.message.startsWith("AJAXError:  (400): ") &&
                    e.error.message.indexOf("layerViewId="+this.layerView.id) > -1) {
                    this.handleSuperclusterExpired();
                }
            });

            this._onReady.resolve();
        });
    }

    private async onUpdateThematic(thematic: any): Promise<void> {
        if (thematic.layerViewId !== this.layerView.id) {
            return Promise.resolve();
        }
        this.update({isThematicChanged: true});
    }

    private async reloadSupercluster() {
        if (!this._datasource) {
            console.warn("reloadSupercluster called with no datasource for the layer.")
            return;
        }
        this.eventChannel.fireEvent("onShowLoadingOverlay");
        const datasourceFilter = this.getDatasourceFilter(this._colorThematic, this._symbolThematic);
        await this.datasourceManager.recreate(this._datasource.getId(), this.layerView, this.layerInfo, datasourceFilter);
        this.eventChannel.fireEvent("onHideLoadingOverlay");
    }

    private async createLayers() {
        this._colorThematic = await this.stylerFactory.getColorStyler(this.layerView).getThematic(this.layerView);
        this._sizeThematic = await this.stylerFactory.getSizeStyler().getThematic(this.layerView);
        this._symbolThematic = await this.stylerFactory.getSymbolStyler().getThematic(this.layerView);

        if (!this._datasource) {
            this._datasource = await this.datasourceManager.create(this.layerView, this.layerInfo, true, this.getDatasourceFilter(this._colorThematic, this._symbolThematic));
        }

        await this._bubbleLayer.create(this._datasource, this._colorThematic, this._sizeThematic);
        await this._symbolLayer.create(this._datasource, this._colorThematic, this._sizeThematic, this._symbolThematic);
        await this._labelLayer.create(this._datasource, this._colorThematic, this._sizeThematic);

        if (isPieClusteringOn(this.layerView)) { // isHeatMapDisabledXorIsOverlayPinEnabled(this.layerView)
            this._pieClusterLayer = new PieClusterLayer(this.map, this.layerView, this.layerInfo, this.datasourceManager, this.stylerFactory, this.eventChannel, this._datasource, this._colorThematic);
        }

        if(isMouseoverLabellingEnabled(this.layerView)){
            this._mouseoverLabelHandler = new AzureMouseoverLabelHandler(this.map, this);
        }
    }


    public async update(layerUpdateMetadata: LayerUpdateMetadata): Promise<void> {

        if (this.destroyed) {
            return Promise.resolve();
        }
        console.log("PointLayer.update", this.updatePromise);
        await this.updatePromise;

        this.updatePromise = this.doUpdate(layerUpdateMetadata);
        await this.updatePromise;
        this.eventChannel.fireEvent("onUpdateLayerComplete", this);
    }

    private async doUpdate(layerUpdateMetadata: LayerUpdateMetadata): Promise<void> {

        const isReloadRequired = !!(
            layerUpdateMetadata?.isCriteriaChanged ||
            layerUpdateMetadata?.isMissingAttributes ||
            layerUpdateMetadata?.isThematicChanged ||
            layerUpdateMetadata?.isFeatureInfoLastModifiedChanged ||
            layerUpdateMetadata?.isPieClusteringChanged ||
            layerUpdateMetadata?.isMouseoverLabellingChanged ||
            layerUpdateMetadata?.isHeatmapOverlayPinsChanged ||
            layerUpdateMetadata?.isLabellingChanged ||
            layerUpdateMetadata?.isForceReload
        );

        this._bubbleLayer.setLayerFilter(this._layerFilter);
        this._symbolLayer.setLayerFilter(this._layerFilter);
        this._labelLayer.setLayerFilter(this._layerFilter);

        if (this._datasource && isReloadRequired) {

            this._colorThematic = await this.stylerFactory.getColorStyler(this.layerView).getThematic(this.layerView);
            this._symbolThematic = await this.stylerFactory.getSymbolStyler().getThematic(this.layerView);
            this._sizeThematic = await this.stylerFactory.getSizeStyler().getThematic(this.layerView);

            this._pieClusterLayer?.destroy();

            if(layerUpdateMetadata?.isMouseoverLabellingChanged){
                this._mouseoverLabelHandler?.destroy();
            }

            const datasourceFilter = this.getDatasourceFilter(this._colorThematic, this._symbolThematic);

            if (this.datasourceManager.isGeoJson(this._datasource.getId())) {
                this._bubbleLayer.remove();
                this._symbolLayer.remove();
                this._labelLayer.remove();

                this._datasource = await this.datasourceManager.recreate(this._datasource.getId(), this.layerView, this.layerInfo, datasourceFilter);

                await this._bubbleLayer.recreate(this.layerView, this.layerInfo, this._datasource, this._colorThematic, this._sizeThematic);
                await this._symbolLayer.recreate(this.layerView, this.layerInfo, this._datasource, this._colorThematic, this._sizeThematic, this._symbolThematic);
                await this._labelLayer.recreate(this.layerView, this.layerInfo, this._datasource, this._colorThematic, this._sizeThematic);

            } else { // isPbf
                this.eventChannel.fireEvent("onShowLoadingOverlay");
                await this.datasourceManager.recreate(this._datasource.getId(), this.layerView, this.layerInfo, datasourceFilter);
                await this._bubbleLayer.update(this.layerView, this.layerInfo);
                await this._labelLayer.update(this.layerView, this.layerInfo);
                this.eventChannel.fireEvent("onHideLoadingOverlay");
            }

            if (isPieClusteringOn(this.layerView)) { // isHeatMapDisabledXorIsOverlayPinEnabled(this.layerView)
                this._pieClusterLayer = new PieClusterLayer(this.map, this.layerView, this.layerInfo, this.datasourceManager, this.stylerFactory, this.eventChannel, this._datasource, this._colorThematic);
            }

            if(isMouseoverLabellingEnabled(this.layerView)){
                this._mouseoverLabelHandler = new AzureMouseoverLabelHandler(this.map, this);
            }
        }
        else {
            if (isBubbleLayer(this.layerView, this.layerInfo)) {
                this._symbolLayer.hide();
                await this._bubbleLayer.update(this.layerView, this.layerInfo);
            } else {
                this._bubbleLayer.hide();
                await this._symbolLayer.update(this.layerView, this.layerInfo);
            }
            await this._labelLayer.update(this.layerView, this.layerInfo);

            if (isPieClusteringOn(this.layerView)) {
                await this._pieClusterLayer?.update({});
            }
        }
    }

    protected getColorThematicFilter(colorThematic?: ColorThematic): ExpressionFilterSpecification | undefined {
        return this.stylerFactory.getColorStyler(this.layerView).getFilterExpression(this.layerView, colorThematic);
    }

    protected getSymbolThematicFilter(symbolThematic?: SymbolThematic): ExpressionFilterSpecification | undefined {
        return this.stylerFactory.getSymbolStyler().getFilterExpression(this.layerView, symbolThematic);
    }

    protected hideFeature(featureId: number) {
        this.bubbleLayer.hideFeature(featureId);
        this.symbolLayer.hideFeature(featureId);
        this.labelLayer.hideFeature(featureId);
    }

    protected showFeatures() {
        this.bubbleLayer.showFeatures();
        this.symbolLayer.showFeatures();
        this.labelLayer.showFeatures();
    }

    public getLayerIds(): string[] {
        const layerIds = [
            this.bubbleLayer.getLayerId(),
            this.symbolLayer.getLayerId(),
            this.labelLayer.getLayerId()
        ];

        if (this._pieClusterLayer) {
            layerIds.push(...this._pieClusterLayer.getLayerIds());
        }
        return layerIds;
    }

    public async getClusterLeavesFeats(clusterId: number): Promise<any[]> {
        return new Promise((resolve, _reject) => {

            if (this._datasource && this._datasource instanceof source.DataSource) {
                this._datasource.getClusterLeaves(clusterId, Infinity, 0).then((points) => {
                    const feats: data.Feature<data.Geometry, any>[] = [];
                    points.forEach(p => {
                        const pt = (p as data.Feature<data.Geometry, any>);
                        if (pt?.id) {
                            const id: number|undefined = !isNaN(pt.id) ? +pt.id : undefined;
                            if (id) {
                                feats.push(pt);
                            }
                        }
                    });
                    resolve(feats);
                }).catch(_err => {
                    //es.log.warn("PointLayer : error getting cluster leaves", err);
                    resolve([]);
                });
            } else {
                es.api.layerView.pbfGetClusterChildren(this.layerView.id, clusterId, {suppressDefaultErrorHandling: true}).then((feats: any) => {
                    resolve(feats.map((f: any) => GeomUtils.convertPbfFeatToGeoJsonFeat(f)));
                }).fail((_rs: any, jqXHR: any) => {
                    if(jqXHR?.responseText?.startsWith("SUPERCLUSTER_EXPIRED")) {
                        this.handleSuperclusterExpired();
                    }
                });
            }
        }) as Promise<any[]>;
    }

    // TODO: can generate a lot of feat data that is then dioscarded. add option to rest method to only return ids?
    public async getClusterLeavesFeatIds(clusterId: number): Promise<number[]> {
        const feats = await this.getClusterLeavesFeats(clusterId);
        return feats.map((feat) => {
            const pt = feat as data.Feature<data.Geometry, any>;
            return pt.id && !isNaN(pt?.id) ? +pt.id : undefined;
        }).filter((id): id is number => id !== undefined);
    }

    public destroy(): void {
        super.destroy();
        this._pieClusterLayer?.destroy();
        this._bubbleLayer.remove();
        this._symbolLayer.remove();
        this._labelLayer.remove();
        if (this._datasource) {
            this.datasourceManager.remove(this._datasource.getId());
        }
        es.api.getEventChannel().removeListener('onUpdateThematic', this.debouncedOnUpdateThematic);
    }

    public hide() {
        this.bubbleLayer.hide();
        this.symbolLayer.hide();
        this.labelLayer.hide();
        this._pieClusterLayer?.hide();
    }

    public show() {
        if (isBubbleLayer(this.layerView, this.layerInfo)) {
            this.bubbleLayer.show();
        } else {
            this.symbolLayer.show();
        }

        this.labelLayer.show();
        this._pieClusterLayer?.show();
    }

    public get bubbleLayer(): AzureBubbleLayer {
        return this._bubbleLayer;
    }

    public get symbolLayer(): AzureSymbolLayer {
        return this._symbolLayer;
    }

    public get labelLayer(): AzureLabelLayer {
        return this._labelLayer;
    }

    public get colorThematic(): ColorThematic | undefined {
        return this._colorThematic;
    }

    public get sizeThematic(): SizeThematic | undefined {
        return this._sizeThematic;
    }

    public get symbolThematic(): SymbolThematic | undefined {
        return this._symbolThematic;
    }

    public get colorStyler(): ColorThematicStyler {
        return this._colorStyler;
    }

    public get sizeStyler(): SizeThematicStyler {
        return this._sizeStyler;
    }

    public get pieClusterLayer(): PieClusterLayer | undefined {
        return this._pieClusterLayer;
    }

    public getActiveAzureLayerId(): string | undefined {
        if (isBubbleLayer(this.layerView, this.layerInfo)) {
            return this.bubbleLayer.getLayerId();
        } else if(isPieClusteringOn(this.layerView)){
            return this._pieClusterLayer?.getActiveAzureLayerId();
        } else {
            return this.symbolLayer.getLayerId();
        }
    }
}
